import { type BreadcrumbList, type ListItem, type Product, type RealEstateListing, type WithContext } from 'schema-dts';
import { useMemo } from 'react';
import { getListingPrice } from '@pkgs/components/utils/listingDataUtils';
import { type AddressComponent, type Currency, type Listing } from '@pkgs/api';

import { metaDescriptionsExpose } from '@/consts/metaDescriptions';

import { useSettings } from './useSettings';

type JsonLdObject = {
  productJsonLd?: WithContext<Product>;
  realEstateListingJsonLd?: WithContext<RealEstateListing>;
  breadcrumbListJsonLd?: WithContext<BreadcrumbList>;
};

export const useJsonLd = (title: string, listing: Listing, description?: string, imageUrl?: string): JsonLdObject => {
  const { currency, language } = useSettings();
  const priceDescriptor = getListingPrice(listing, { currency, language });
  const priceRange = listing.basePrice?.[priceDescriptor.fieldName];
  const priceValue = priceRange?.min ?? priceRange?.max;
  const priceJsonLdFormattedValue = priceValue ? new Intl.NumberFormat('en', { useGrouping: false }).format(priceValue) : undefined;

  const jsonLdObject: JsonLdObject = useMemo(() => {
    const result: JsonLdObject = {
      productJsonLd: undefined,
      realEstateListingJsonLd: undefined,
      breadcrumbListJsonLd: undefined,
    };
    if (!listing.hasPriceOnRequest && priceJsonLdFormattedValue && title) {
      result.productJsonLd = getProductJsonLd(priceJsonLdFormattedValue, title, description, listing.baseCurrency, imageUrl);
    }
    if (title) {
      result.realEstateListingJsonLd = getRealEstateListingJsonLd(title, imageUrl, description, listing.publishedAt);
    }
    result.breadcrumbListJsonLd = getBreadcrumbData(listing.addressComponents);
    return result;
  }, [listing, priceJsonLdFormattedValue, title, description, imageUrl]);
  return jsonLdObject;
};

export const getProductJsonLd = (
  price: string,
  title: string,
  description?: string,
  baseCurrency?: Currency,
  imageUrl?: string,
): WithContext<Product> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: title,
    description: metaDescriptionsExpose(description ?? ''),
    ...(Boolean(imageUrl) && { image: imageUrl }),
    offers: {
      '@type': 'Offer',
      price: price,
      ...(baseCurrency && { priceCurrency: baseCurrency }),
    },
  };
};

export const getRealEstateListingJsonLd = (
  title: string,
  imageUrl?: string,
  description?: string,
  publishedAt?: string,
): WithContext<RealEstateListing> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    ...(publishedAt && { datePosted: publishedAt }),
    name: title,
    ...(Boolean(imageUrl) && { image: imageUrl }),
    description: metaDescriptionsExpose(description ?? ''),
  };
};

export const getBreadcrumbData = (addressComponents: AddressComponent[]): WithContext<BreadcrumbList> => {
  const breadcrumbListJsonLd: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: getListItemJsonLd(addressComponents),
  };
  return breadcrumbListJsonLd;
};

export const getListItemJsonLd = (addressComponents: AddressComponent[]) => {
  const isValid = (a: AddressComponent): a is WithRequired<AddressComponent, 'text' | 'autogenUrl'> => Boolean(a.text && a.autogenUrl);
  return addressComponents.filter(isValid).map<ListItem>(({ text, autogenUrl }, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: text,
    item: `https://www.engelvoelkers.com${autogenUrl}`,
  }));
};
